import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../../../../reduxService/hooks';
import { requestSubmit, clearLastSubmitResult, clearSubmissionErrors, setAccordionToOpen } from '../../../../../store/form/formControlSlice';
import { EProjectType } from '../../../../../enum/project';
import { logDebug, LogTag } from '../../../../../utils/utils';
import { IProject, IDynamicProjectOutputData } from '../../../../../api/IApiResponse';
import { StepItem } from '../types';

export interface UseStepperNavigationProps {
    steps: StepItem[];
    computedActiveStep: number;
    dynamicProject: IProject | null | undefined;
    outputData: IDynamicProjectOutputData | null | undefined;
    projectIdFromUrl?: string;
    isApiLoading: boolean;
    isFormProcessing: boolean;
    setErrorDialogContent: (content: React.ReactNode | null) => void;
    setIsApiLoading: (loading: boolean) => void;
}

export interface UseStepperNavigationReturn {
    handleStep: (stepItem: StepItem, targetIndex: number) => void;
    isStepDisabled: (targetStepIndex: number) => boolean;
}

/**
 * Hook for managing stepper navigation logic
 * Handles step navigation, validation, and automatic redirects
 */
export const useStepperNavigation = ({
    steps,
    computedActiveStep,
    dynamicProject,
    outputData,
    projectIdFromUrl,
    isApiLoading,
    isFormProcessing,
    setErrorDialogContent,
    setIsApiLoading,
}: UseStepperNavigationProps): UseStepperNavigationReturn => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    /**
     * Determines if a step should be disabled based on business logic
     */
    const isStepDisabled = useCallback(
        (targetStepIndex: number): boolean => {
            const targetStep = steps[targetStepIndex];
            if (!targetStep) return true;

            if (targetStepIndex === computedActiveStep) {
                return true;
            }

            const projectGuid = dynamicProject?.guid;
            const currentProjectType = dynamicProject?.type;

            if (targetStepIndex < computedActiveStep) {
                if (targetStep.endpoint.includes(':id') && !projectGuid) {
                    return true;
                }
                return false;
            }

            if (currentProjectType === EProjectType.geogrid) {
                switch (targetStep.id) {
                    case 'project_type':
                        return !projectGuid;
                    case 'geogrid_design':
                        return !projectGuid || !dynamicProject?.type;
                    case 'geogrid_results':
                        if (dynamicProject?.geogridHasBeenRan) return false;
                        return !projectGuid || steps[computedActiveStep]?.id !== 'geogrid_design';
                    default:
                        return true;
                }
            } else {
                switch (targetStep.id) {
                    case 'project_type':
                        return !projectGuid;
                    case 'design_condition':
                        return !projectGuid || !dynamicProject?.type;
                    case 'results':
                        if (dynamicProject?.hasBeenRan) return false;
                        return !projectGuid || steps[computedActiveStep]?.id !== 'design_condition';
                    case 'geogrid_design':
                        return !projectGuid || (steps[computedActiveStep]?.id !== 'design_condition' && steps[computedActiveStep]?.id !== 'results' && !dynamicProject?.hasBeenRan);
                    case 'geogrid_results':
                        if (dynamicProject?.geogridHasBeenRan) return false;
                        return !projectGuid || steps[computedActiveStep]?.id !== 'geogrid_design';
                    default:
                        return true;
                }
            }
        },
        [computedActiveStep, dynamicProject, steps]
    );

    /**
     * Handles step navigation click
     */
    const handleStep = useCallback(
        (stepItem: StepItem, targetIndex: number) => {
            if (isStepDisabled(targetIndex)) {
                return;
            }

            if (isApiLoading || isFormProcessing) {
                return;
            }

            dispatch(clearLastSubmitResult());
            dispatch(clearSubmissionErrors());
            dispatch(setAccordionToOpen(null));
            setErrorDialogContent(null);

            const projectGuid = dynamicProject?.guid;

            if (stepItem.isRunStep) {
                const hasBeenRun = stepItem.hasBeenRunFlag;
                const source = stepItem.id === 'results' ? 'StepperRun' : 'StepperGeoGridRun';

                const hasValidOutputData = stepItem.id === 'results' ? !!(dynamicProject?.hasBeenRan || outputData) : !!dynamicProject?.geogridHasBeenRan;

                if ((hasBeenRun || hasValidOutputData) && projectGuid) {
                    logDebug('Stepper', `Navigating to ${stepItem.id} - hasBeenRun: ${hasBeenRun}, hasValidOutputData: ${hasValidOutputData}`, undefined, LogTag.NAVIGATION);
                    navigate(stepItem.endpoint.replace(':id', projectGuid));
                } else if (projectGuid) {
                    logDebug('Stepper', `No valid results found for ${stepItem.id} - triggering calculation`, { hasBeenRun, hasValidOutputData }, LogTag.NAVIGATION);
                    setIsApiLoading(true);
                    dispatch(requestSubmit({ source }));
                } else {
                    const errorMsg = `Cannot run calculation: ${!projectGuid ? 'Project ID missing' : 'System busy'}`;
                    setErrorDialogContent(errorMsg);
                }
                return;
            }

            let targetPath = stepItem.endpoint;

            if (stepItem.endpoint.includes('/:id?')) {
                targetPath = stepItem.endpoint.replace('/:id?', projectGuid ? `/${projectGuid}` : '');
            } else if (stepItem.endpoint.includes('/:id')) {
                if (projectGuid) {
                    targetPath = stepItem.endpoint.replace(':id', projectGuid);
                } else {
                    const errorMsg = t('errors.navigation.missingGuidForStep', 'Cannot navigate to step: Project ID is missing.');
                    setErrorDialogContent(errorMsg);
                    return;
                }
            }

            navigate(targetPath);
        },
        [isStepDisabled, isApiLoading, isFormProcessing, dispatch, setErrorDialogContent, dynamicProject, outputData, navigate, t, setIsApiLoading]
    );

    // 🔧 REMOVED: Automatic redirect logic (DUPLICATE - causes race conditions)
    // REASON: This effect was duplicating the redirect logic in useFormEffects.ts
    // When user modifies input on /results page:
    //   1. useFormEffects correctly resets hasBeenRan=false and redirects to /designCondition
    //   2. BUT this effect would ALSO trigger and cause race conditions with calculation logic
    // SOLUTION: Let useFormEffects be the SINGLE source of truth for redirect behavior
    // useStepperNavigation should only handle MANUAL step clicks, not automatic redirects
    //
    // NOTE: Geogrid redirect logic might need similar review if issues arise there

    return {
        handleStep,
        isStepDisabled,
    };
};
